import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api as axios } from '../api/api';
import { SnackbarContext } from '../App';
import { ClipboardList, Users, ArrowLeft, Trash2, UserPlus, Search, Clock, Calendar, CheckCircle2, Mail, Plus, Layers, Tag, Download, Filter, MoreHorizontal, ExternalLink } from "lucide-react";
import { format } from 'date-fns';
import SequenceEnrollModal from './SequenceEnrollModal';
import AddToCampaignModal from './AddToCampaignModal';
import PageHeader from './ui/PageHeader';
import PageShell from './ui/PageShell';
import EmptyState from './ui/EmptyState';
import StatCard from './ui/StatCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useConfirm } from '../contexts/ConfirmContext';
import DataTable from './ui/DataTable';

export default function CrmListDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const openConfirm = useConfirm();

    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [stageFilter, setStageFilter] = useState('ALL');
    const [readinessFilter, setReadinessFilter] = useState('ALL');
    const [selectedMembers, setSelectedMembers] = useState([]);

    // Add Contacts Modal State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [availableContacts, setAvailableContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [contactSearch, setContactSearch] = useState('');
    const [selectedContactIds, setSelectedContactIds] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [contactModalPage, setContactModalPage] = useState(1);
    const [contactModalTotalPages, setContactModalTotalPages] = useState(1);

    // Sequence Modal
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    // Campaign Modal
    const [isAddCampaignOpen, setIsAddCampaignOpen] = useState(false);
    const [campaignTargetIds, setCampaignTargetIds] = useState([]);
    
    // Bulk Operations
    const [isBulkStageOpen, setIsBulkStageOpen] = useState(false);
    const [bulkStageValue, setBulkStageValue] = useState('LEAD');
    const [isBulkTagOpen, setIsBulkTagOpen] = useState(false);
    const [bulkTagValue, setBulkTagValue] = useState('');
    const [bulkTagMode, setBulkTagMode] = useState('add');
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const fetchListDetail = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/prospect-lists/${id}`);
            setList(response.data.data);
        } catch (error) {
            console.error('Error fetching list details:', error);
            showSnackbar(error.response?.data?.message || 'Error loading list', 'error');
            navigate('/crm/lists');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchListDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleRemoveMember = async (personMasterId) => {
        openConfirm({
            title: 'Remove from List?',
            message: 'Are you sure you want to remove this contact from the list? They will remain in your workspace.',
            confirmText: 'Remove from List',
            variant: 'destructive',
            onConfirm: async () => {
                try {
                    await axios.delete(`/api/prospect-lists/${id}/items/${personMasterId}`);
                    showSnackbar('Contact removed from list', 'success');
                    fetchListDetail();
                } catch (err) {
                    showSnackbar(err.response?.data?.message || 'Error removing contact', 'error');
                }
            }
        });
    };

    const fetchWorkspaceContacts = async (page = 1) => {
        setLoadingContacts(true);
        try {
            // Using existing contacts endpoint to pull workspace contacts
            const params = new URLSearchParams({ page, limit: 100 });
            if (contactSearch) params.append('search', contactSearch);
            const response = await axios.get(`/api/contacts?${params.toString()}`);
            setAvailableContacts(response.data.data || []);
            setContactModalTotalPages(response.data.pagination?.totalPages || 1);
            setContactModalPage(page);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            showSnackbar('Error loading workspace contacts', 'error');
        } finally {
            setLoadingContacts(false);
        }
    };

    useEffect(() => {
        if (isAddModalOpen) {
            setContactModalPage(1);
            fetchWorkspaceContacts(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddModalOpen, contactSearch]);

    const toggleContactSelection = (personId) => {
        const newSet = new Set(selectedContactIds);
        if (newSet.has(personId)) newSet.delete(personId);
        else newSet.add(personId);
        setSelectedContactIds(newSet);
    };

    const handleSelectAllOnPage = () => {
        const newSet = new Set(selectedContactIds);
        availableContacts.forEach(contact => {
            const pid = contact.personId;
            if (pid) newSet.add(pid);
        });
        setSelectedContactIds(newSet);
    };

    const handleClearSelection = () => {
        setSelectedContactIds(new Set());
    };

    const handleAddSelectedToTopList = async () => {
        if (selectedContactIds.size === 0) return;
        setIsSaving(true);
        try {
            const personIds = Array.from(selectedContactIds);
            const res = await axios.post(`/api/prospect-lists/${id}/add`, { personIds });
            showSnackbar(`Successfully added ${res.data.added} contacts (${res.data.alreadyInList} already existed)`, 'success');
            setIsAddModalOpen(false);
            setSelectedContactIds(new Set());
            setContactSearch('');
            fetchListDetail();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Error adding contacts', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenCampaignModal = () => {
        if (selectedMembers.length === 0) return;
        // Open the inline Add-to-Campaign modal instead of navigating away
        setCampaignTargetIds(selectedMembers);
        setIsAddCampaignOpen(true);
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/contacts/export', {
                contactIds: selectedMembers
            }, {
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob',
                validateStatus: (s) => s < 500
            });

            if (res.status === 202) {
                const text = await res.data.text();
                const parsed = JSON.parse(text);
                showSnackbar(parsed.message || 'Export requires approval', 'warning');
                return;
            }

            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'crm_list_export.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            showSnackbar('Export successful', 'success');
        } catch (error) {
            showSnackbar('Export failed', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleBulkStageUpdate = async () => {
        if (selectedMembers.length === 0) return;
        setIsBulkProcessing(true);
        try {
            // selectedMembers are workspaceContactIds (contactId field from list items)
            const res = await axios.post(`/api/contacts/bulk-update`,
                { contactIds: selectedMembers, updates: { stage: bulkStageValue } }
            );
            showSnackbar(`${res.data?.modified || selectedMembers.length} contacts' stages updated`, 'success');
            setIsBulkStageOpen(false);
            setSelectedMembers([]);
            fetchListDetail(); 
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Bulk update failed', 'error');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleBulkTagAdd = async () => {
        if (selectedMembers.length === 0 || !bulkTagValue.trim()) return;
        setIsBulkProcessing(true);
        try {
            const newTags = bulkTagValue.split(',').map(t => t.trim()).filter(Boolean);
            const res = await axios.post(`/api/contacts/bulk-tags`,
                { contactIds: selectedMembers, mode: bulkTagMode, tags: newTags }
            );
            showSnackbar(`${res.data?.modified || selectedMembers.length} contacts tagged successfully`, 'success');
            setIsBulkTagOpen(false);
            setBulkTagValue('');
            setSelectedMembers([]);
            fetchListDetail();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Bulk tag failed', 'error');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleBulkRemove = async () => {
        if (selectedMembers.length === 0) return;
        openConfirm({
            title: 'Remove from List?',
            message: `Are you sure you want to remove ${selectedMembers.length} contacts from this list?`,
            confirmText: 'Remove from List',
            variant: 'destructive',
            onConfirm: async () => {
                setIsBulkProcessing(true);
                try {
                    // Build a contactId → personMasterId lookup from the loaded items
                    // The remove API requires personMasterId, but selectedMembers holds contactIds
                    const contactToPersonMap = {};
                    (list?.items || []).forEach(item => {
                        if (item.contactId) contactToPersonMap[item.contactId.toString()] = item._id;
                    });
                    const personMasterIds = selectedMembers
                        .map(cid => contactToPersonMap[cid.toString()])
                        .filter(Boolean);

                    if (personMasterIds.length === 0) {
                        showSnackbar('Could not resolve contacts to remove', 'error');
                        return;
                    }
                    await Promise.all(personMasterIds.map(personMasterId => 
                        axios.delete(`/api/prospect-lists/${id}/items/${personMasterId}`)
                    ));
                    showSnackbar(`Successfully removed ${personMasterIds.length} members from list`, 'success');
                    setSelectedMembers([]);
                    fetchListDetail();
                } catch (err) {
                    showSnackbar(err.response?.data?.message || 'Error removing contacts', 'error');
                } finally {
                    setIsBulkProcessing(false);
                }
            }
        });
    };

    if (loading) {
        return (
            <PageShell>
                <div className="animate-pulse space-y-8">
                    <div className="bg-muted h-32 rounded-[2rem]"></div>
                    <div className="bg-muted h-96 rounded-[2.5rem]"></div>
                </div>
            </PageShell>
        );
    }

    if (!list) return null;

    const items = list.items || [];
    const filteredItems = items.filter(item => {
        // Search
        const matchesSearch = !searchQuery || 
            (item.firstName || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
            (item.lastName || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
            (item.canonicalEmail || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.companyName || '').toLowerCase().includes(searchQuery.toLowerCase());
        
        // Stage
        const matchesStage = stageFilter === 'ALL' || item.stage === stageFilter;
        
        // Readiness
        const matchesReadiness = readinessFilter === 'ALL' || 
            (readinessFilter === 'VALID' && item.canonicalEmail) ||
            (readinessFilter === 'NO_EMAIL' && !item.canonicalEmail) ||
            (readinessFilter === 'ACTIVE' && item.stage && item.stage !== 'UNQUALIFIED' && item.stage !== 'INVALID');

        return matchesSearch && matchesStage && matchesReadiness;
    });

    const totalMembers = items.length;
    const recentlyAdded = items.filter(i => new Date(i.addedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length;
    const activeMembers = items.filter(i => i.stage && i.stage !== 'UNQUALIFIED' && i.stage !== 'INVALID').length;
    const validEmails = items.filter(i => i.canonicalEmail).length;

    return (
        <PageShell className="px-4 sm:px-6 lg:px-8 w-full">
            <PageHeader 
                title={list.name}
                subtitle={list.description || "Manage the CRM contacts specifically targeted in this list."}
                icon={ClipboardList}
                iconClassName="shadow-glow shadow-primary/20"
                actions={
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" onClick={() => navigate('/crm/lists')} className="h-11 px-5 rounded-2xl font-bold border border-border/50 text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Lists
                        </Button>
                        <Button onClick={() => setIsAddModalOpen(true)} className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white h-11 px-6 rounded-2xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95">
                            <UserPlus className="w-4 h-4 mr-2" /> Add Contacts
                        </Button>
                    </div>
                }
            />

            <div className="space-y-6 md:space-y-8 mt-6">
                
                {selectedMembers.length > 0 && (
                    <div className="sticky top-0 z-10 flex items-center justify-between bg-card backdrop-blur-md p-3 rounded-2xl border border-primary/20 shadow-xl px-4 lg:px-6 mb-6 animate-in slide-in-from-top-4 duration-300 w-full overflow-x-auto hide-scrollbar">
                        {/* LEFT SIDE: Selection & Clear */}
                        <div className="flex items-center gap-3 shrink-0">
                            <span className="px-3 py-1 bg-primary text-primary-foreground text-[11px] font-black uppercase tracking-widest rounded-full shadow-sm">
                                {selectedMembers.length} Selected
                            </span>
                            <Button onClick={() => setSelectedMembers([])} variant="ghost" size="sm" className="h-7 px-2 text-[10px] uppercase tracking-widest font-black text-muted-foreground hover:text-foreground rounded-lg">
                                Clear
                            </Button>
                            <div className="h-6 w-px bg-border mx-1 hidden sm:block delay-100" />
                        </div>

                        {/* CENTER: Primary High-Visibility Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                            <Button onClick={() => setIsEnrollModalOpen(true)} className="h-9 px-4 text-xs font-black uppercase tracking-widest bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-500/20 border-0 gap-2 rounded-xl transition-all">
                                <Mail className="w-4 h-4" /> <span className="hidden sm:inline">Add to</span> Sequence
                            </Button>
                            <Button onClick={handleOpenCampaignModal} className="h-9 px-4 text-xs font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/20 border-0 gap-2 rounded-xl transition-all">
                                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add to</span> Campaign
                            </Button>

                            {/* CENTER: Secondary Operational Wraps (Hidden on Mobile) */}
                            <div className="hidden lg:flex items-center gap-2 ml-2">
                                <Button onClick={() => setIsBulkStageOpen(true)} variant="outline" className="h-9 px-4 text-xs font-black uppercase tracking-widest border-border hover:bg-primary/5 gap-2 rounded-xl transition-all">
                                    <Layers className="w-4 h-4" /> Stage
                                </Button>
                                <Button onClick={() => setIsBulkTagOpen(true)} variant="outline" className="h-9 px-4 text-xs font-black uppercase tracking-widest border-border hover:bg-primary/5 gap-2 rounded-xl transition-all">
                                    <Tag className="w-4 h-4" /> Tag
                                </Button>
                                <Button disabled={isExporting} onClick={handleExport} variant="outline" className="h-9 px-4 text-xs font-black uppercase tracking-widest border-border hover:bg-primary/5 gap-2 rounded-xl transition-all">
                                    <Download className="w-4 h-4" /> {isExporting ? 'Exporting...' : 'Export'}
                                </Button>
                            </div>

                            {/* CENTER: Secondary Mobile Overflow Menus */}
                            <div className="lg:hidden ml-1">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="h-9 w-9 p-0 rounded-xl border-border hover:bg-muted/50 transition-colors">
                                            <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-xl border-border/50 py-1">
                                        <DropdownMenuLabel className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-3">Manage</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setIsBulkStageOpen(true)} className="font-bold cursor-pointer text-xs py-2 px-3 focus:bg-primary/5"><Layers className="w-4 h-4 mr-2 opacity-70"/> Change Stage</DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setIsBulkTagOpen(true)} className="font-bold cursor-pointer text-xs py-2 px-3 focus:bg-primary/5"><Tag className="w-4 h-4 mr-2 opacity-70"/> Add Tag</DropdownMenuItem>
                                        <DropdownMenuItem disabled={isExporting} onClick={handleExport} className="font-bold cursor-pointer text-xs py-2 px-3 focus:bg-primary/5"><Download className="w-4 h-4 mr-2 opacity-70"/> Export</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>

                        {/* RIGHT: Safe Destructive Demarcation */}
                        <div className="flex items-center shrink-0 ml-auto pl-4 border-l border-border/50">
                            <Button disabled={isBulkProcessing} onClick={handleBulkRemove} variant="ghost" className="h-9 px-3 text-xs font-black uppercase tracking-widest text-destructive hover:bg-destructive/10 gap-2 rounded-lg transition-all">
                                <Trash2 className="w-4 h-4" /> <span className="hidden sm:inline">{isBulkProcessing ? 'Removing...' : 'Remove from List'}</span>
                            </Button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard
                    title="Total Members"
                    value={totalMembers}
                    icon={Users}
                    description="Contacts isolated in this list"
                />
                <StatCard
                    title="Recently Added"
                    value={recentlyAdded}
                    icon={Clock}
                    description="Contacts added in last 7 days"
                    variant="success"
                />
                <StatCard
                    title="List Readiness"
                    value={activeMembers}
                    icon={CheckCircle2}
                    description="Qualified operational contacts"
                    variant="warning"
                />
                <StatCard
                    title="Deliverability"
                    value={validEmails}
                    icon={Mail}
                    description="Members with valid canonical emails"
                    trend={`${totalMembers ? Math.round((validEmails / totalMembers) * 100) : 0}% Valid`}
                />
            </div>

                <div className="bg-card rounded-[2.5rem] border border-border shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 md:p-6 pb-0 border-b border-border/50">
                        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-6">
                            <div className="relative w-full md:max-w-md">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search list members..."
                                    className="pl-11 h-12 rounded-2xl border-2 border-border/50 bg-muted/30 shadow-sm font-semibold transition-colors focus:bg-background"
                                />
                            </div>
                            <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0 hide-scrollbar shrink-0">
                                <Select value={stageFilter} onValueChange={setStageFilter}>
                                    <SelectTrigger className="w-[140px] bg-muted/30 border-2 border-border/50 text-foreground font-semibold rounded-2xl h-12 shadow-sm">
                                        <Filter className="w-4 h-4 mr-2 opacity-50" />
                                        <SelectValue placeholder="Stage" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">All Stages</SelectItem>
                                        <SelectItem value="LEAD">Lead</SelectItem>
                                        <SelectItem value="CONTACTED">Contacted</SelectItem>
                                        <SelectItem value="REPLIED">Replied</SelectItem>
                                        <SelectItem value="MEETING_BOOKED">Meeting Booked</SelectItem>
                                        <SelectItem value="CLOSED_WON">Closed Won</SelectItem>
                                        <SelectItem value="CLOSED_LOST">Closed Lost</SelectItem>
                                    </SelectContent>
                                </Select>

                                <Select value={readinessFilter} onValueChange={setReadinessFilter}>
                                    <SelectTrigger className="w-[155px] bg-muted/30 border-2 border-border/50 text-foreground font-semibold rounded-2xl h-12 shadow-sm">
                                        <Filter className="w-4 h-4 mr-2 opacity-50" />
                                        <SelectValue placeholder="Readiness" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">All Readiness</SelectItem>
                                        <SelectItem value="VALID">Valid Email</SelectItem>
                                        <SelectItem value="NO_EMAIL">Missing Email</SelectItem>
                                        <SelectItem value="ACTIVE">Active Stage</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <DataTable
                    selectable={{
                        selectedIds: selectedMembers,
                        onSelectionChange: setSelectedMembers,
                        // Key selection on `contactId` (workspace contact _id), not personMasterId
                        rowId: (row) => row.contactId ? row.contactId.toString() : row._id?.toString()
                    }}
                    columns={[
                        {
                            header: 'Contact',
                            accessor: 'name',
                            render: (item) => (
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-black text-sm uppercase">
                                        {(item.firstName?.[0] || item.canonicalEmail?.[0] || '?')}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-bold text-sm">
                                            {item.firstName || item.lastName ? `${item.firstName || ''} ${item.lastName || ''}`.trim() : 'Unknown Name'}
                                        </span>
                                        <span className="text-xs text-muted-foreground font-medium">{item.canonicalEmail}</span>
                                    </div>
                                </div>
                            )
                        },
                        {
                            header: 'Company / Role',
                            accessor: 'company',
                            render: (item) => (
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold">{item.companyName || '-'}</span>
                                    <span className="text-xs text-muted-foreground">{item.jobTitle || 'No Title'}</span>
                                </div>
                            )
                        },
                        {
                            header: 'Status',
                            accessor: 'stage',
                            render: (item) => (
                                <div className="flex items-center">
                                    <span className="bg-muted px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                                        {item.stage || 'LEAD'}
                                    </span>
                                </div>
                            )
                        },
                        {
                            header: 'Added',
                            accessor: 'addedAt',
                            render: (item) => (
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                                    <Calendar className="w-3.5 h-3.5 opacity-70" />
                                    {item.addedAt ? format(new Date(item.addedAt), 'MMM d, yyyy') : 'Recently'}
                                </div>
                            )
                        },
                        {
                            header: '',
                            accessor: 'actions',
                            align: 'right',
                            render: (item) => (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted/50">
                                            <span className="sr-only">Open menu</span>
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-[180px] rounded-2xl shadow-xl border-border/50">
                                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground font-black px-2 pb-1">Actions</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={() => { 
                                                const contactId = item.contactId;
                                                if (contactId) {
                                                    navigate(`/crm/contacts?focus=${contactId}`);
                                                } else {
                                                    showSnackbar('No workspace CRM record found for this contact', 'warning');
                                                }
                                            }} 
                                            className="cursor-pointer font-bold rounded-xl text-xs py-2">
                                            <ExternalLink className="mr-2 h-4 w-4 opacity-70" /> View Contact
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => { 
                                            // Use contactId (workspace contact _id) for sequence enrollment
                                            const cid = item.contactId;
                                            if (cid) { setSelectedMembers([cid.toString()]); setIsEnrollModalOpen(true); }
                                        }} className="cursor-pointer font-bold rounded-xl text-xs py-2">
                                            <Mail className="mr-2 h-4 w-4 opacity-70 text-violet-500" /> Add to Sequence
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => {
                                            setCampaignTargetIds([item.contactId || item._id]);
                                            setIsAddCampaignOpen(true);
                                        }} className="cursor-pointer font-bold rounded-xl text-xs py-2">
                                            <Plus className="mr-2 h-4 w-4 opacity-70 text-indigo-500" /> Add to Campaign
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => handleRemoveMember(item._id)} className="cursor-pointer font-bold text-destructive focus:text-destructive focus:bg-destructive/10 rounded-xl text-xs py-2">
                                            <Trash2 className="mr-2 h-4 w-4 opacity-70" /> Remove from List
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )
                        }
                    ]}
                    data={filteredItems}
                    loading={false}
                    emptyState={
                        <div className="flex justify-center items-center py-20 bg-background/50">
                            <EmptyState 
                                title={items.length === 0 ? "List is empty" : "No matches found"}
                                description={items.length === 0 ? "Add your first CRM contact to this list to start orchestrating outreach." : "Try adjusting your search criteria."}
                                icon={Users}
                                action={
                                    items.length === 0 && (
                                        <Button onClick={() => setIsAddModalOpen(true)} className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white mt-6 h-11 px-5 rounded-2xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95">
                                            <UserPlus className="w-4 h-4 mr-2" /> Select Contacts
                                        </Button>
                                    )
                                }
                            />
                        </div>
                    }
                    pagination={{
                        page: 1,
                        limit: filteredItems.length > 0 ? filteredItems.length : 10,
                        totalRecords: filteredItems.length,
                        totalPages: 1
                    }}
                />
               </div>
            </div>

            {/* Add Contacts Modal */}
            <Sheet open={isAddModalOpen} onOpenChange={(open) => {
                if (!open) {
                    setIsAddModalOpen(false);
                    setSelectedContactIds(new Set());
                    setContactSearch('');
                }
            }}>
                <SheetContent side="right" size="xl" className="p-0 flex flex-col h-[85vh]">
                    <div className="p-6 bg-muted/20 border-b border-border/50">
                        <SheetHeader>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                                    <UserPlus className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <SheetTitle className="text-2xl font-black">Add to List</SheetTitle>
                                    <SheetDescription className="text-sm font-medium text-muted-foreground">
                                        Select workspace contacts to add to "{list.name}". Duplicates will be safely ignored.
                                    </SheetDescription>
                                </div>
                            </div>
                        </SheetHeader>
                    </div>

                    <div className="px-8 py-4 border-b border-border/50 flex items-center justify-between gap-4 bg-muted/10">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input 
                                value={contactSearch}
                                onChange={(e) => setContactSearch(e.target.value)}
                                placeholder="Search workspace contacts..."
                                className="pl-11 h-12 rounded-2xl border-2 border-border/50 bg-card shadow-sm font-medium"
                            />
                        </div>
                        {availableContacts.length > 0 && (
                            <div className="flex items-center shrink-0 bg-background p-1 rounded-xl border border-border pb-1">
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={handleSelectAllOnPage}
                                    className="h-10 px-4 text-xs font-bold rounded-lg hover:bg-muted shadow-sm transition-all"
                                >
                                    Select Page
                                </Button>
                                <div className="w-px h-4 bg-border mx-1 flex-shrink-0" />
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={handleClearSelection}
                                    disabled={selectedContactIds.size === 0}
                                    className="h-10 px-4 text-xs font-bold rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
                                >
                                    Clear Selection
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 px-8 bg-card/50">
                        {loadingContacts ? (
                            <div className="flex py-10 justify-center">
                                <span className="text-muted-foreground font-black uppercase text-xs tracking-widest animate-pulse">Loading Contacts...</span>
                            </div>
                        ) : availableContacts.length === 0 ? (
                            <div className="py-20 text-center">
                                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                <p className="text-muted-foreground font-medium block">
                                    {contactSearch ? "No contacts match your search." : "No workspace contacts available."}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {availableContacts.map(contact => {
                                    // Use the shaped flat payload returned by /api/contacts
                                    const personId = contact.personId;
                                    if (!personId) return null; // List items strictly require a master ID bound
                                    
                                    const isSelected = selectedContactIds.has(personId);
                                    
                                    return (
                                        <div 
                                            key={contact.id || contact._id} 
                                            onClick={() => toggleContactSelection(personId)}
                                            className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-transparent hover:bg-muted/50 hover:border-border/60'}`}
                                        >
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30 bg-card'}`}>
                                                {isSelected && <CheckCircle2 className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-sm truncate">
                                                    {contact.displayName !== "Unknown" ? contact.displayName : contact.displayEmail}
                                                </h4>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {contact.jobTitle && <span className="mr-2">{contact.jobTitle}</span>}
                                                    <span className="opacity-60">{contact.companyName || contact.displayEmail}</span>
                                                </p>
                                            </div>
                                            <div className="shrink-0 text-xs font-medium text-muted-foreground">
                                                {contact.stage && <span className="bg-muted px-2 py-1 rounded-md">{contact.stage}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        {contactModalTotalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 px-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => fetchWorkspaceContacts(contactModalPage - 1)}
                                    disabled={contactModalPage === 1 || loadingContacts}
                                    className="h-9 font-bold rounded-xl"
                                >
                                    Previous
                                </Button>
                                <span className="text-xs font-bold text-muted-foreground">
                                    Page {contactModalPage} of {contactModalTotalPages}
                                </span>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => fetchWorkspaceContacts(contactModalPage + 1)}
                                    disabled={contactModalPage === contactModalTotalPages || loadingContacts}
                                    className="h-9 font-bold rounded-xl"
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t border-border/50 bg-background flex items-center justify-between shrink-0">
                        <span className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-2">
                            {selectedContactIds.size} Selected
                        </span>
                        <div className="flex gap-3">
                            <Button 
                                variant="ghost" 
                                onClick={() => setIsAddModalOpen(false)}
                                className="h-11 px-6 rounded-2xl font-bold"
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleAddSelectedToTopList}
                                disabled={selectedContactIds.size === 0 || isSaving}
                                className="bg-primary hover:bg-primary/90 text-white h-11 px-8 rounded-xl font-bold shadow-lg shadow-primary/20"
                            >
                                {isSaving ? 'Adding...' : `Add ${selectedContactIds.size > 0 ? selectedContactIds.size : ''} to List`}
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Add to Campaign Modal */}
            {isAddCampaignOpen && (
                <AddToCampaignModal
                    workspaceContactIds={campaignTargetIds}
                    onClose={() => { setIsAddCampaignOpen(false); setCampaignTargetIds([]); }}
                    onComplete={() => {
                        setIsAddCampaignOpen(false);
                        setCampaignTargetIds([]);
                        setSelectedMembers([]);
                        fetchListDetail();
                    }}
                />
            )}

            {/* Sequence Enrollment Modal */}
            {isEnrollModalOpen && (
                <SequenceEnrollModal
                    workspaceContactIds={selectedMembers}
                    onClose={() => setIsEnrollModalOpen(false)}
                    onComplete={() => {
                        setIsEnrollModalOpen(false);
                        setSelectedMembers([]);
                        fetchListDetail();
                        showSnackbar(`${selectedMembers.length} contact enrollment process completed`, 'success');
                    }}
                />
            )}

            {/* Bulk Stage Modal */}
            <Sheet open={isBulkStageOpen} onOpenChange={setIsBulkStageOpen}>
                <SheetContent side="right" className="p-0">
                    <SheetHeader className="p-6 bg-muted/20 border-b border-border/50">
                        <SheetTitle>Toplu Aşama Güncelleme</SheetTitle>
                        <SheetDescription className="font-medium">
                            Seçilen {selectedMembers.length} kişinin aşamasını güncelleyin.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="py-4">
                        <Select value={bulkStageValue} onValueChange={setBulkStageValue}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="LEAD">Lead</SelectItem>
                                <SelectItem value="CONTACTED">Contacted</SelectItem>
                                <SelectItem value="REPLIED">Replied</SelectItem>
                                <SelectItem value="MEETING_BOOKED">Meeting Booked</SelectItem>
                                <SelectItem value="CLOSED_WON">Closed Won</SelectItem>
                                <SelectItem value="CLOSED_LOST">Closed Lost</SelectItem>
                                <SelectItem value="UNQUALIFIED">Unqualified</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <SheetFooter className="p-4 bg-muted/20 border-t border-border/50">
                        <Button variant="outline" onClick={() => setIsBulkStageOpen(false)}>İptal</Button>
                        <Button onClick={handleBulkStageUpdate} disabled={isBulkProcessing}>
                            {isBulkProcessing ? 'Güncelleniyor...' : 'Güncelle'}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Bulk Tag Modal */}
            <Sheet open={isBulkTagOpen} onOpenChange={setIsBulkTagOpen}>
                <SheetContent side="right" className="p-0">
                    <SheetHeader className="p-6 bg-muted/20 border-b border-border/50">
                        <SheetTitle>Toplu Etiket İşlemi</SheetTitle>
                        <SheetDescription className="font-medium">
                            Seçilen {selectedMembers.length} kişi üzerinde etiket işlemi uygulayın.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="py-4 space-y-3">
                        <Select value={bulkTagMode} onValueChange={setBulkTagMode}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="add">Ekle (Mevcut etiketlere ekle)</SelectItem>
                                <SelectItem value="remove">Kaldır (Bu etiketleri çıkar)</SelectItem>
                                <SelectItem value="replace">Değiştir (Tümü bu etiketlerle değiştirilir)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input
                            value={bulkTagValue}
                            onChange={(e) => setBulkTagValue(e.target.value)}
                            placeholder="tag1, tag2, tag3"
                        />
                    </div>
                    <SheetFooter className="p-4 bg-muted/20 border-t border-border/50">
                        <Button variant="outline" onClick={() => setIsBulkTagOpen(false)}>İptal</Button>
                        <Button onClick={handleBulkTagAdd} disabled={isBulkProcessing || !bulkTagValue.trim()}>
                            {isBulkProcessing ? 'İşleniyor...' : bulkTagMode === 'add' ? 'Ekle' : bulkTagMode === 'remove' ? 'Kaldır' : 'Değiştir'}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

        </PageShell>
    );
}
